import React from "react";
import { Button, Form, Row, Col, Select, Space, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

const { Text } = Typography;

function idKey(value) {
  return value === undefined || value === null ? "" : String(value);
}

function buildOptions(items, selectedValues, noun) {
  const options = (items || []).map((item) => ({
    value: item.id,
    label: item.name || `${noun} ${item.id}`,
  }));
  const known = new Set(options.map((option) => idKey(option.value)));
  (selectedValues || []).forEach((value) => {
    if (value !== undefined && value !== null && !known.has(idKey(value))) {
      known.add(idKey(value));
      options.push({ value, label: `${noun} ${value}` });
    }
  });
  return options;
}

export default function SessionClassScopes({
  form,
  classes,
  streams,
  disabled = false,
}) {
  const scopes = Form.useWatch("classScopes", form) || [];

  return (
    <Form.List
      name="classScopes"
      rules={[
        {
          validator: async (_, value) => {
            if (!Array.isArray(value) || value.length === 0) {
              throw new Error("Select at least one class.");
            }
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {fields.map((field, index) => {
            const current = scopes[field.name] || {};
            const selectedClassIDs = scopes
              .filter((_, rowIndex) => rowIndex !== field.name)
              .map((scope) => scope && scope.classID)
              .filter((value) => value !== undefined && value !== null);
            const classOptions = buildOptions(
              classes,
              [current.classID],
              "Class",
            ).map((option) => ({
              ...option,
              disabled: selectedClassIDs.some(
                (value) => idKey(value) === idKey(option.value),
              ),
            }));
            const streamOptions = buildOptions(
              streams,
              current.streamIDs,
              "Stream",
            );

            return (
              <div
                key={field.key}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <Row gutter={[16, 0]} align="top">
                  <Col xs={24} md={10}>
                    <Form.Item
                      {...field}
                      name={[field.name, "classID"]}
                      label={`Class ${index + 1}`}
                      rules={[
                        { required: true, message: "Select a class." },
                        {
                          validator: async (_, value) => {
                            if (
                              value !== undefined &&
                              value !== null &&
                              selectedClassIDs.some(
                                (selected) => idKey(selected) === idKey(value),
                              )
                            ) {
                              throw new Error(
                                "Each class can be selected once.",
                              );
                            }
                          },
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select class"
                        options={classOptions}
                        disabled={disabled}
                        showSearch
                        optionFilterProp="label"
                        onChange={() => {
                          form.setFieldValue(
                            ["classScopes", field.name, "streamIDs"],
                            [],
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      {...field}
                      name={[field.name, "streamIDs"]}
                      label="Streams"
                    >
                      <Select
                        mode="multiple"
                        allowClear
                        placeholder="All streams in this class"
                        options={streamOptions}
                        disabled={disabled || !current.classID}
                        showSearch
                        optionFilterProp="label"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={2}>
                    <Form.Item label=" ">
                      <Button
                        aria-label={`Remove class ${index + 1}`}
                        danger
                        icon={<DeleteOutlined />}
                        disabled={disabled || fields.length <= 1}
                        onClick={() => remove(field.name)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Text type="secondary">
                  Leave streams empty to include the whole class.
                </Text>
              </div>
            );
          })}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            disabled={disabled}
            onClick={() => add({ streamIDs: [] })}
            block
          >
            Add class
          </Button>
          <Form.ErrorList errors={errors} />
        </Space>
      )}
    </Form.List>
  );
}
